import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import styled from 'styled-components';
import { Button, Grid } from 'ui-toolkit';
import { v4 as uuid } from 'uuid';

const SortableHandleButton = styled(Button)`
  color: ${(props) => props.theme.colors.contentPlaceholder};
`;

const SortableListItemGrid = styled(Grid)`
  &.on-sort {
    z-index: 210;
  }
`;

const Handle = SortableHandle(() => {
  return (
    <SortableHandleButton intent="ghost" isInline>
      <FontAwesomeIcon icon={faGripVertical} size="lg" />
    </SortableHandleButton>
  );
});

interface ISortableItemProps {
  item: any;
  content?: (item: any) => React.ReactNode;
  onDelete?: (item: any) => void;
  showDelete?: boolean;
}

const SortableListItem = SortableElement(({ content, item, onDelete, showDelete }: ISortableItemProps) => {
  const deleteItem = () => onDelete && onDelete(item);

  return (
    <SortableListItemGrid key={uuid()} templateColumns={'min-content 1fr min-content'}>
      <Handle />
      {content && content(item)}
      {showDelete && (
        <Button intent="ghost" isInline onClick={deleteItem}>
          <FontAwesomeIcon icon={faTrash} color={'red'} />
        </Button>
      )}
    </SortableListItemGrid>
  );
});

interface ISortableComponentProps {
  items: any[];
  content?: (item: any) => React.ReactNode;
  onDelete?: (item: any) => void;
  showDelete?: boolean;
}

const SortableComponent = SortableContainer(({ items, content, onDelete, showDelete }: ISortableComponentProps) => {
  return (
    <Grid>
      {items.map((item, index) => (
        <SortableListItem
          key={uuid()}
          item={item}
          content={content}
          onDelete={onDelete}
          showDelete={showDelete}
          index={index}
        />
      ))}
    </Grid>
  );
});

interface ISortableListProps {
  items: any[];
  content?: (item: any) => React.ReactNode;
  onDelete?: (item: any) => void;
  onSort: (items: any[]) => void;
  showDelete?: boolean;
}

export const SortableList: React.FC<ISortableListProps> = ({
  items = [],
  content,
  onDelete,
  onSort,
  showDelete = true,
}) => {
  const onSortHandler = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (onSort) {
      onSort(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <SortableComponent
      helperClass="on-sort"
      items={items}
      onDelete={onDelete}
      content={content}
      lockAxis="y"
      useDragHandle={true}
      onSortEnd={onSortHandler}
      showDelete={showDelete}
    />
  );
};
